import React from 'react';
import useSeo from '../lib/useSeo';
import useJsonLd from '../lib/useJsonLd';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import Reveal from '../components/site/Reveal';
import CountUp from '../components/site/CountUp';
import AudienceIntelligenceMockup from '../components/site/AudienceIntelligenceMockup';
import { track, events } from '../lib/analytics';
import {
  FileBarChart,
  Users2,
  Swords,
  CalendarRange,
  FileText,
  Landmark,
  Radio,
  Sigma,
  ChevronRight,
  ArrowRight,
  Check,
} from 'lucide-react';

const briefingContents = [
  {
    icon: Users2,
    tag: 'Section 1',
    title: 'Decision architecture',
    body:
      'Named decision-makers, influencers, and blockers mapped for the account, with tenure, prior role, and public commentary that shows how they buy.',
  },
  {
    icon: FileBarChart,
    tag: 'Section 2',
    title: 'Contract stack & renewals',
    body:
      'Sized, sourced material contract renewals and the trigger windows around them — where budget is genuinely up for grabs.',
  },
  {
    icon: Swords,
    tag: 'Section 3',
    title: 'Competitive set',
    body:
      "Who's already inside, who's circling, and where the gaps are that give the advertiser a defensible way in.",
  },
  {
    icon: CalendarRange,
    tag: 'Section 4',
    title: '12-week path to close',
    body:
      'A dated sequence of specific plays — the outreach, the assets, the meetings — that put the advertiser in front of the account when it matters most.',
  },
];

const sources = [
  { icon: FileText, label: 'SEC filings', body: '10-Ks, 10-Qs, proxies and 8-K disclosures' },
  { icon: Radio, label: 'Earnings calls', body: 'Transcript analysis, tone, guidance shifts' },
  { icon: Landmark, label: 'Primary interviews', body: 'Executive and market interviews on the record' },
  { icon: Sigma, label: 'Analytical estimates', body: 'Modelled projections calibrated to disclosures' },
];

const stats = [
  { v: '13', k: 'Pages per briefing' },
  { v: '4', k: 'Source classes' },
  { v: '12 wk', k: 'Path to close' },
  { v: '48 hr', k: 'Turnaround' },
];

const AudienceIntelligencePage = () => {
  useSeo({
    title: 'Audience Intelligence — Know exactly who your readers buy for',
    description: 'Audience Intelligence turns publisher first-party data into a live map of buyer roles, industries and intent — the foundation of every 5DBS lead-gen programme.',
    path: '/products/audience-intelligence',
  });
  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://fivedbs.com/products/audience-intelligence#service',
      name: 'Audience Intelligence',
      alternateName: '5DBS Audience Intelligence',
      serviceType: 'B2B first-party audience analytics and buyer-graph mapping',
      description: 'Audience Intelligence turns a publisher\'s first-party audience data into a live map of buyer roles, seniorities, industries and intent signals — the foundation of every 5DBS lead-generation programme.',
      provider: { '@id': 'https://fivedbs.com/#organization' },
      areaServed: [
        { '@type': 'Place', name: 'Global' },
        { '@type': 'Place', name: 'North America' },
        { '@type': 'Place', name: 'United Kingdom' },
        { '@type': 'Place', name: 'European Union' },
        { '@type': 'Place', name: 'APAC' },
      ],
      audience: {
        '@type': 'Audience',
        audienceType: 'B2B publishers, editorial teams and revenue/insight leaders',
      },
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        url: 'https://fivedbs.com/contact',
        priceCurrency: 'USD',
      },
      url: 'https://fivedbs.com/products/audience-intelligence',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fivedbs.com/' },
        { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://fivedbs.com/#products' },
        { '@type': 'ListItem', position: 3, name: 'Audience Intelligence', item: 'https://fivedbs.com/products/audience-intelligence' },
      ],
    },
  ]);
  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main>
        {/* Hero */}
        <section
          className="relative min-h-[70vh] flex items-center overflow-hidden pt-28"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(20,184,166,0.28) 0%, rgba(20,30,52,0.90) 60%, rgba(20,30,52,0.95) 100%), url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1920&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="relative max-w-6xl mx-auto w-full px-6 lg:px-8 py-20">
            <nav className="flex items-center gap-2 text-slate-300 text-[12px] font-bold uppercase tracking-[0.14em] mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight size={14} />
              <Link to="/#products" className="hover:text-white">Products</Link>
              <ChevronRight size={14} />
              <span className="text-white">Audience Intelligence</span>
            </nav>
            <p className="text-teal-300 text-[12px] font-bold tracking-[0.16em] uppercase mb-5">
              Product · Audience Intelligence
            </p>
            <h1 className="text-white font-black tracking-[-0.01em] leading-[1.05] text-[44px] sm:text-[58px] md:text-[68px] max-w-[900px]">
              A dated, sized, named path to close.
            </h1>
            <p className="mt-7 text-slate-200/90 text-[17px] leading-[1.7] max-w-[720px]">
              Publisher-branded corporate intelligence briefings that map decision-makers, size material contract renewals, chart the competitive set, and hand the advertiser's sales team a 12-week plan.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="ai-hero-request-briefing"
                onClick={() => track(events.REQUEST_SAMPLE_BRIEFING_CLICK, { location: 'hero' })}
                className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-teal-500 hover:bg-teal-600 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-[0_4px_11px_0_rgba(20,184,166,0.4)] hover:shadow-[0_14px_26px_-12px_rgba(20,184,166,0.5)] hover:-translate-y-0.5 transition-all duration-300"
              >
                Request a sample briefing
                <ArrowRight size={16} className="ml-2" />
              </Link>
              <Link
                to="/#products"
                className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-white/[0.06] backdrop-blur border border-white/25 text-white text-[13px] font-bold uppercase tracking-[0.08em] hover:bg-white/[0.12] transition-all duration-300"
              >
                Explore all products
              </Link>
            </div>
          </div>
        </section>

        {/* Machine-readable answer block — surfaces direct answers to "What is Audience Intelligence?"
            style queries in AI answer engines (ChatGPT, Perplexity, Google AI Overviews). */}
        <section className="bg-white py-14 border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-8">
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">What is Audience Intelligence?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">Audience Intelligence is 5DBS's first-party audience analytics service. It turns a B2B publisher's own audience data — content engagement, form submissions, newsletter signals, events — into a live, machine-readable map of buyer roles, seniorities, industries and intent. It is the foundation of every 5DBS lead-generation programme.</p>
            </div>
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">Who is Audience Intelligence for?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">Audience Intelligence is built for B2B publishers, editorial and audience-development teams, and revenue leaders who need a shared source of truth about who their audience actually is. It fits any team preparing to launch, price or defend a direct-sold lead-gen programme.</p>
            </div>
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">How does Audience Intelligence work?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">Audience Intelligence unifies a publisher's first-party signals under a single identity graph, appends firmographic and intent data, and exposes the result as dashboards and APIs. Editorial and revenue teams use it to plan content, price campaigns and prove ICP coverage to their advertisers.</p>
            </div>
          </div>
        </section>

        {/* Stats band */}
        <section className="bg-brand-soft py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <Reveal key={s.k} delay={i * 90} className="text-center">
                <div className="text-teal-600 text-[36px] sm:text-[42px] font-black leading-none tracking-tight">
                  <CountUp value={s.v} />
                </div>
                <p className="mt-3 text-body-muted text-[13.5px] font-semibold">{s.k}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Product mockup — sample briefing */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <p className="text-teal-600 text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                Sample briefing
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                What lands in your advertiser's inbox
              </h2>
              <p className="mt-5 text-body-muted text-[16px] leading-[1.75]">
                A redacted first page from a real briefing — dated sections, sized renewals, mapped decision-makers, cited sources.
              </p>
            </div>
            <Reveal className="max-w-5xl mx-auto">
              <AudienceIntelligenceMockup />
              <p className="mt-4 text-center text-body-muted text-[13px] italic">
                Page 1 of 13 · Redacted for illustration.
              </p>
            </Reveal>
          </div>
        </section>

        {/* What's inside a briefing */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-teal-600 text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                What's inside
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                Four sections. One decisive briefing.
              </h2>
              <p className="mt-6 text-body-muted text-[16px] leading-[1.75]">
                13 pages, delivered under your brand, ready to hand to the advertiser's sales lead the moment the account meeting is set.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
              {briefingContents.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal
                    key={f.title}
                    delay={i * 110}
                    className="bg-white rounded-2xl shadow-mk p-7 flex gap-5 hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-teal-600 to-teal-400 text-white shadow-[0_4px_11px_0_rgba(20,184,166,0.35)]">
                      <Icon size={22} strokeWidth={2.2} />
                    </div>
                    <div>
                      <span className="inline-block text-teal-700 text-[10.5px] font-bold uppercase tracking-[0.14em] bg-teal-50 rounded-full px-2.5 py-0.5">
                        {f.tag}
                      </span>
                      <h3 className="mt-3 text-navy text-[19px] font-bold">{f.title}</h3>
                      <p className="mt-2 text-body-muted text-[14.5px] leading-[1.7]">{f.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Sources */}
        <section className="bg-brand-soft py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-teal-600 text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                Built from
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                Four source classes. Nothing scraped.
              </h2>
              <p className="mt-6 text-body-muted text-[16px] leading-[1.75]">
                Every claim in a briefing is traceable to primary or filed material — with citation footnotes on the page it appears.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {sources.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal
                    key={s.label}
                    delay={i * 90}
                    className="bg-white rounded-2xl shadow-mk p-6 hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700">
                      <Icon size={20} strokeWidth={2.4} />
                    </div>
                    <h3 className="mt-5 text-navy text-[16px] font-bold">{s.label}</h3>
                    <p className="mt-2 text-body-muted text-[13.5px] leading-[1.6]">{s.body}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Guarantees */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-teal-600 text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
              What you can promise the advertiser
            </p>
            <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
              Every claim cited. Every recommendation dated.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                'Fully footnoted — every figure sourced',
                'White-labelled to your brand',
                'Delivered in 48 hours from brief',
              ].map((c) => (
                <div
                  key={c}
                  className="flex items-center gap-3 rounded-2xl bg-brand-soft border border-slate-100 px-5 py-4"
                >
                  <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-600 to-teal-400 text-white flex items-center justify-center shrink-0">
                    <Check size={16} strokeWidth={3} />
                  </span>
                  <span className="text-navy text-[13.5px] font-bold text-left">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="relative py-24"
          style={{ background: 'linear-gradient(135deg, #1A2035 0%, #344767 100%)' }}
        >
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-white font-black tracking-[-0.01em] leading-[1.15] text-[30px] sm:text-[40px]">
              Bring one account. We'll build the briefing.
            </h2>
            <p className="mt-5 text-slate-300 text-[16px] leading-[1.7]">
              Send us one named target account. In 48 hours you'll have a redacted sample of the briefing you could hand your advertiser — under your brand.
            </p>
            <div className="mt-9">
              <Link
                to="/contact"
                data-testid="ai-cta-request-briefing"
                onClick={() => track(events.REQUEST_SAMPLE_BRIEFING_CLICK, { location: 'cta' })}
                className="inline-flex items-center justify-center h-12 px-8 rounded-md bg-teal-500 hover:bg-teal-600 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-[0_4px_11px_0_rgba(20,184,166,0.4)] hover:shadow-[0_14px_26px_-12px_rgba(20,184,166,0.5)] hover:-translate-y-0.5 transition-all duration-300"
              >
                Request a sample briefing
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AudienceIntelligencePage;
